/* Lo que apunta el autor durante el viaje: hecho o saltado, notas, gastos y casillas.
   Se guarda al momento en el móvil y se sube a viajes-api cuando hay red. Si el mismo dato
   cambia en dos sitios, gana el cambio más reciente (el servidor aplica la misma regla). */
import { useSyncExternalStore } from 'react';
import { noAutorizado, registrarLimpieza } from './sesion.ts';
import { VIAJE } from './viaje.ts';

export type Tipo = 'marca' | 'casilla' | 'gasto' | 'ajuste' | 'frase' | 'compra';
export interface Registro<D = unknown> { tipo: Tipo; id: string; datos: D | null; borrado: boolean; actualizado: number }

interface Estado {
  cursor: number;                          // última versión del servidor que tenemos
  registros: Record<string, Registro>;
  pendientes: string[];                    // claves cambiadas aquí y aún sin subir
  sync: 'ok' | 'pendiente' | 'sin-red' | 'error';
  ultimaSync: number | null;
}

const CLAVE = `tabiji.apuntes.${VIAJE}`;
const clave = (tipo: Tipo, id: string) => `${tipo}|${id}`;

function inicial(): Estado {
  try {
    const e = JSON.parse(localStorage.getItem(CLAVE) ?? 'null');
    if (e?.registros) return { ...e, sync: e.pendientes?.length ? 'pendiente' : 'ok' };
  } catch { /* copia rota: se empieza de cero y el servidor la rellena */ }
  return { cursor: 0, registros: {}, pendientes: [], sync: 'ok', ultimaSync: null };
}

let estado = inicial();
const oyentes = new Set<() => void>();

function cambiar(nuevo: Estado) {
  estado = nuevo;
  try {
    localStorage.setItem(CLAVE, JSON.stringify({ cursor: nuevo.cursor, registros: nuevo.registros, pendientes: nuevo.pendientes, ultimaSync: nuevo.ultimaSync }));
  } catch { /* lleno: se queda en memoria hasta subirlo */ }
  oyentes.forEach(f => f());
}

export function useApuntes(): Estado {
  return useSyncExternalStore(f => { oyentes.add(f); return () => oyentes.delete(f); }, () => estado);
}

/** Si una casilla está hecha. Se suscribe solo a ese valor: marcar una no repinta las demás. */
export function useCasillaHecha(id: string): boolean {
  return useSyncExternalStore(f => { oyentes.add(f); return () => oyentes.delete(f); }, () => !!(estado.registros[`casilla|${id}`] && !estado.registros[`casilla|${id}`].borrado && (estado.registros[`casilla|${id}`].datos as { hecho?: boolean } | null)?.hecho));
}

export function leer<D>(tipo: Tipo, id: string): D | null {
  const r = estado.registros[clave(tipo, id)];
  return r && !r.borrado ? (r.datos as D) : null;
}

export function lista<D>(tipo: Tipo): Registro<D>[] {
  return Object.values(estado.registros).filter(r => r.tipo === tipo && !r.borrado) as Registro<D>[];
}

/** Apunta (o borra, con `null`) un dato. Se guarda ya y se sube en cuanto se pueda. */
export function poner(tipo: Tipo, id: string, datos: unknown | null) {
  const k = clave(tipo, id);
  const previo = estado.registros[k];
  const r: Registro = { tipo, id, datos: datos ?? null, borrado: datos == null, actualizado: Math.max(Date.now(), (previo?.actualizado ?? 0) + 1) };
  cambiar({
    ...estado,
    registros: { ...estado.registros, [k]: r },
    pendientes: estado.pendientes.includes(k) ? estado.pendientes : [...estado.pendientes, k],
    sync: 'pendiente',
  });
  programar(1200);
}

/** El servidor respondió, pero mal (distinto de «no se llega»: sin red, wifi sin salida, Pi apagada). */
class ErrorServidor extends Error {}

let enCurso: Promise<void> | null = null;
let temporizador = 0;
function programar(ms: number) {
  clearTimeout(temporizador);
  temporizador = window.setTimeout(() => { void sincronizar(); }, ms);
}

/** Sube lo pendiente y trae lo que haya cambiado en otro dispositivo. */
export function sincronizar(): Promise<void> {
  if (enCurso) return enCurso;
  enCurso = (async () => {
    if (!navigator.onLine) { if (estado.pendientes.length) cambiar({ ...estado, sync: 'sin-red' }); return; }
    const enviados = estado.pendientes.map(k => estado.registros[k]).filter(Boolean);
    try {
      const r = await fetch(`/api/viajes/${VIAJE}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ desde: estado.cursor, cambios: enviados }),
        signal: AbortSignal.timeout(15_000),      // una red que no contesta no puede dejar la sincronización esperando para siempre
      });
      if (r.status === 401) noAutorizado();            // la sesión caducó: se pide entrar; lo pendiente se queda en el móvil
      if (!r.ok) throw new ErrorServidor(String(r.status));
      const resp = (await r.json()) as { version: number; registros: (Registro & { version: number })[] };
      const registros = { ...estado.registros };
      for (const x of resp.registros) {
        const k = clave(x.tipo, x.id), local = registros[k];
        if (!local || x.actualizado >= local.actualizado) registros[k] = { tipo: x.tipo, id: x.id, datos: x.datos, borrado: x.borrado, actualizado: x.actualizado };
      }
      // Lo enviado queda subido salvo que se haya vuelto a cambiar mientras viajaba
      const subidos = new Map(enviados.map(e => [clave(e.tipo, e.id), e.actualizado]));
      const pendientes = estado.pendientes.filter(k => subidos.get(k) !== registros[k]?.actualizado);
      cambiar({ cursor: resp.version, registros, pendientes, sync: pendientes.length ? 'pendiente' : 'ok', ultimaSync: Date.now() });
      if (pendientes.length) programar(500);
    } catch (e) {
      cambiar({ ...estado, sync: e instanceof ErrorServidor ? 'error' : 'sin-red' });
      programar(30_000);
    }
  })().finally(() => { enCurso = null; });
  return enCurso;
}

/** Cambio de persona o cierre de sesión en este móvil: se empieza de cero (lo de la otra persona no se queda aquí). */
export function reiniciar() {
  clearTimeout(temporizador);
  cambiar({ cursor: 0, registros: {}, pendientes: [], sync: 'ok', ultimaSync: null });
}
registrarLimpieza(reiniciar);

/* Cuándo sincronizar: al abrir, al volver la red, al volver a la app y cada minuto con ella delante */
export function arrancarSincronizacion() {
  void sincronizar();
  const alVolverRed = () => void sincronizar();
  const alVolver = () => { if (document.visibilityState === 'visible') void sincronizar(); };
  addEventListener('online', alVolverRed);
  document.addEventListener('visibilitychange', alVolver);
  const i = setInterval(alVolver, 60_000);
  return () => { removeEventListener('online', alVolverRed); document.removeEventListener('visibilitychange', alVolver); clearInterval(i); };
}

/* ───────── Atajos para lo que usa la ventanilla ───────── */
export type EstadoParada = 'hecho' | 'saltado';
/** Se guarda también el minuto del reloj del viaje en que se marcó: así se sabe si se va tarde. */
export interface Marca { estado: EstadoParada; min?: number; dia?: number }
export const marcaDe = (bloque: string) => leer<Marca>('marca', bloque);
export const estadoDe = (bloque: string) => marcaDe(bloque)?.estado;
export const notaDe = (bloque: string) => leer<{ texto: string }>('marca', `nota:${bloque}`)?.texto ?? '';
export const marcar = (bloque: string, e: EstadoParada | null, cuando?: { min: number; dia: number }) =>
  poner('marca', bloque, e ? { estado: e, ...cuando } : null);
export const anotar = (bloque: string, texto: string) => poner('marca', `nota:${bloque}`, texto.trim() ? { texto } : null);

/* Casillas: preparativos, compras, tareas de los documentos */
export const casillaHecha = (id: string) => !!leer<{ hecho: boolean }>('casilla', id)?.hecho;
export const marcarCasilla = (id: string, hecho: boolean) => poner('casilla', id, hecho ? { hecho: true } : null);

/* El cambio ¥/€ lo fija el autor (15b); si no, el de la hoja de gastos */
export const cambioDe = (porDefecto: number) => leer<{ valor: number }>('ajuste', 'yenPorEuro')?.valor ?? porDefecto;
export const ponerCambio = (valor: number | null) => poner('ajuste', 'yenPorEuro', valor ? { valor } : null);

/* Gastos: en yenes o en euros; los totales se calculan con el cambio de cada momento */
/** `para`: para quién es (Yo, Familia, Amigos, Pareja o un nombre); `regalo`: qué regalo es, si lo es. Los gastos son de cada uno. */
export interface Gasto { dia: number; hora: string; yen?: number; eur?: number; cat: string; concepto: string; para?: string; regalo?: string }
export const gastos = () => lista<Gasto>('gasto').map(r => r.datos!).filter(Boolean);
export const gastosConId = () => lista<Gasto>('gasto').filter(r => r.datos).map(r => ({ id: r.id, ...r.datos! }));
export const cambiarGasto = (id: string, g: Gasto | null) => poner('gasto', id, g);
export const enYenes = (g: Gasto, cambio: number) => g.yen ?? Math.round((g.eur ?? 0) * cambio);
export const enEuros = (g: Gasto, cambio: number) => g.eur ?? (g.yen ?? 0) / cambio;
export const apuntarGasto = (g: Gasto) => poner('gasto', `g-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`, g);

/* Frases propias: las que el autor escribe y traduce; viajan con lo demás y se ven sin conexión */
export interface FrasePropia { es: string; ja: string; romaji: string | null; creada: number }
export const misFrases = () => lista<FrasePropia>('frase').filter(r => r.datos).map(r => ({ id: r.id, ...r.datos! })).sort((a, b) => b.creada - a.creada);
export const guardarFrase = (f: Omit<FrasePropia, 'creada'>) => poner('frase', `fr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, { ...f, creada: Date.now() });
export const borrarFrase = (id: string) => poner('frase', id, null);

/* Mi lista de compras (de cada uno): lo que quiero traer, para quién y si ya lo compré */
export interface MiCompra { que: string; para: string; donde?: string; hecho: boolean; idea?: string; creada: number }
export const misCompras = () => lista<MiCompra>('compra').filter(r => r.datos).map(r => ({ id: r.id, ...r.datos! })).sort((a, b) => Number(a.hecho) - Number(b.hecho) || b.creada - a.creada);
export const anadirCompra = (c: Omit<MiCompra, 'creada' | 'hecho'>) => poner('compra', `co-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, { ...c, hecho: false, creada: Date.now() });
export const marcarCompra = (id: string, c: MiCompra, hecho: boolean) => poner('compra', id, { ...c, hecho });
export const borrarCompra = (id: string) => poner('compra', id, null);
